if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => this.setActiveMedia(mediaToSwitch.dataset.target, false, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Illusiv: when the page opens on a specific colour (?variant=), scroll to that slide/thumbnail
        requestAnimationFrame(() => {
          const active = this.elements.viewer.querySelector('.product__media-item.is-active');
          if (active && active.previousElementSibling) this.setActiveMedia(active.dataset.mediaId, false, true);
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        // Illusiv: Also sync variant on mobile swipe
        this.syncVariantFromMedia(mediaId);
      }

      setActiveMedia(mediaId, prepend, skipVariantSync) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        // Illusiv: Sync variant selection when thumbnail is clicked
        if (!skipVariantSync && !prepend) {
          this.syncVariantFromMedia(mediaId);
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Illusiv: vertical thumbnail rail on desktop — scroll the rail, not the row
        const rail = this.elements.thumbnails.slider;
        if (rail && getComputedStyle(rail).flexDirection === 'column') {
          const railRect = rail.getBoundingClientRect();
          const thumbRect = thumbnail.getBoundingClientRect();
          if (thumbRect.top < railRect.top || thumbRect.bottom > railRect.bottom) {
            // Smooth scrolling gets cancelled by the slider's scroll handling, so animate with instant steps
            const from = rail.scrollTop;
            const to = Math.max(0, from + (thumbRect.top - railRect.top) - railRect.height / 2 + thumbRect.height / 2);
            const start = performance.now();
            const step = (now) => {
              const t = Math.min(1, (now - start) / 260);
              const eased = 1 - Math.pow(1 - t, 3);
              rail.scrollTo({ top: from + (to - from) * eased, behavior: 'instant' });
              if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      syncVariantFromMedia(mediaId) {
        // Find the thumbnail that was clicked to get variant options
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!thumbnail || !thumbnail.dataset.variantOptions) return;

        const optionValues = thumbnail.dataset.variantOptions.split('|||');
        if (!optionValues.length) return;

        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return;

        // Only change the COLOR option — leave Size and other options untouched
        // Color is typically associated with images; size is not
        const optionContainers = variantSelects.querySelectorAll('.product-form__input');

        optionValues.forEach((optionValue, index) => {
          const container = optionContainers[index];
          if (!container) return;

          // Check the option label — only change color-related options
          const legend = container.querySelector('legend, label');
          if (!legend) return;
          const optionName = legend.textContent.trim().toLowerCase().replace(/:.*$/, '').trim();

          // Only sync color/colour options — skip size, style, material, etc.
          const colorNames = ['color', 'colour', 'color/design', 'design', 'shade'];
          if (!colorNames.includes(optionName)) return;

          // Try radio/swatch inputs
          const inputs = container.querySelectorAll('input[type="radio"]');
          for (const input of inputs) {
            if (input.value === optionValue && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }

          // Try select dropdown
          const select = container.querySelector('select');
          if (select && select.value !== optionValue) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
